/**
 * AI Workflow Template Browser
 *
 * Template-first landing experience. Shows template cards grouped by industry
 * with typed input field previews and sample I/O in the detail modal.
 */

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Search,
  Clock,
  Layers,
  ChevronRight,
  Check,
  X,
  AlertCircle,
  Loader2,
  Zap,
  ChevronDown,
  ChevronUp,
  FileText,
  PenLine,
  Bookmark,
  Trash2,
  Globe,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';

import { fetchTemplates, fetchTemplateDetail, fetchIndustries, deleteTemplate } from '../api/templatesApi';
import { Button, Card } from './ui';
import OnboardingWizard from './OnboardingWizard';

// Starter-card polling: check every 5s for up to 90s while generation runs
// async in the background (roleserver auth-events consumer, brainstorm-z8t2.5).
const STARTER_POLL_INTERVAL_MS = 5000;
const STARTER_POLL_TIMEOUT_MS = 90000;

// ── Field type icons ───────────────────────────────────────────────────────────
const FIELD_TYPE_LABELS = {
  text: 'Text',
  textarea: 'Long text',
  file_upload: 'File upload',
  date: 'Date',
  select: 'Dropdown',
  number: 'Number',
};

// ── Complexity glyph (3-bar density indicator) ──────────────────────────────
const COMPLEXITY_LEVEL = { low: 1, medium: 2, high: 3 };

function ComplexityGlyph({ level }) {
  const lit = COMPLEXITY_LEVEL[level] || COMPLEXITY_LEVEL.medium;
  return (
    <span
      className="inline-flex items-center gap-0.5"
      title={`Complexity: ${level || 'medium'}`}
      aria-label={`Complexity ${level || 'medium'}`}
    >
      {[1, 2, 3].map((i) => (
        <span
          key={i}
          className={`h-1 w-2 rounded-sm ${i <= lit ? 'bg-gray-700' : 'bg-gray-200'}`}
        />
      ))}
    </span>
  );
}

// ── Template card ─────────────────────────────────────────────────────────────
function TemplateCard({ template, onSelect, isSelected, onDelete }) {
  const [showExample, setShowExample] = useState(false);

  const hasExample = template.sample_input || template.sample_output;
  const fieldCount = template.input_schema?.length || 0;
  const requiredCount = template.input_schema?.filter((f) => f.required).length || 0;
  const durationMin = template.estimated_duration
    ? Math.ceil(template.estimated_duration / 60)
    : null;

  // Meta parts: domain · field count · duration · step count. usage_count hides until hover.
  const metaParts = [
    template.domain && (
      <span key="dom" className="capitalize">{template.domain.toLowerCase()}</span>
    ),
    fieldCount > 0 && (
      <span key="fields" className="tabular-nums">
        {fieldCount} field{fieldCount === 1 ? '' : 's'}
        {requiredCount > 0 && (
          <span className="text-gray-400"> · {requiredCount} required</span>
        )}
      </span>
    ),
    durationMin && (
      <span key="dur" className="inline-flex items-center gap-1 tabular-nums">
        <Clock size={11} />~{durationMin} min
      </span>
    ),
    template.node_count > 0 && (
      <span key="steps" className="inline-flex items-center gap-1 tabular-nums">
        <Layers size={11} />{template.node_count}
      </span>
    ),
  ].filter(Boolean);

  return (
    <div
      className={`group relative w-full text-left p-4 rounded-xl border transition-all duration-200 cursor-pointer ${
        isSelected
          ? 'border-indigo-500 bg-indigo-50/60'
          : 'border-gray-200/70 bg-white hover:border-indigo-300 hover:shadow-sm hover:-translate-y-0.5'
      }`}
      onClick={() => onSelect(template)}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <h3 className="font-semibold text-gray-900 truncate">{template.template_name}</h3>
          {template.has_graph && (
            <span title="Full workflow — instant execution">
              <Zap size={14} className="text-amber-500 flex-shrink-0" />
            </span>
          )}
          {template.complexity && <ComplexityGlyph level={template.complexity} />}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isSelected && <Check size={18} className="text-indigo-600" />}
          {onDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(template); }}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
              title="Delete template"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      <p className="text-sm text-gray-600 mb-3 line-clamp-2" style={{ textWrap: 'pretty' }}>
        {template.description}
      </p>

      {/* Quiet meta line: domain · 4 fields · ~6 min · 8 steps */}
      {metaParts.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-2 flex-wrap">
          {metaParts.map((part, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="text-gray-300">·</span>}
              {part}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Hover-revealed footer: usage stat + secondary actions */}
      <div className="flex items-center justify-between min-h-[20px]">
        <span className="text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity tabular-nums">
          {template.usage_count > 0 ? `Used ${template.usage_count}×` : ''}
        </span>
        <div className="flex items-center gap-3 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          {hasExample && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowExample(v => !v); }}
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 font-medium"
            >
              {showExample ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              example
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onSelect(template); }}
            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
          >
            Use playbook
            <ChevronRight size={13} />
          </button>
        </div>
      </div>

      {showExample && hasExample && (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-2" onClick={(e) => e.stopPropagation()}>
          {template.sample_input && Object.keys(template.sample_input).length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">Example input</div>
              <div className="bg-gray-50 rounded-lg px-3 py-2 space-y-1">
                {Object.entries(template.sample_input).map(([k, v]) => (
                  <div key={k} className="flex gap-2 text-xs">
                    <span className="text-gray-400 shrink-0 capitalize">{k.replace(/_/g, ' ')}:</span>
                    <span className="text-gray-700 truncate">{String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {template.sample_output && (
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">Example output</div>
              <div className="bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-700 whitespace-pre-wrap line-clamp-6 font-mono">
                {template.sample_output}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── "Tell us who you are" framing card ──────────────────────────────────────
function FramingCard({ onSetupStack }) {
  return (
    <Card variant="static" className="p-5 mb-6 flex items-start justify-between gap-4 flex-wrap">
      <div className="flex items-start gap-3 min-w-0">
        <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600 flex-shrink-0">
          <Sparkles size={18} />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 mb-1">Tell us who you are</h3>
          <p className="text-sm text-gray-600 max-w-xl">
            Share your domain and role and we'll design your first-use template stack.
          </p>
        </div>
      </div>
      <Button onClick={onSetupStack} variant="primary" size="md" className="flex-shrink-0">
        Set up my stack
      </Button>
    </Card>
  );
}

// ── "Designing your starter stack…" placeholder ─────────────────────────────
function DesigningStackPlaceholder({ refreshing }) {
  return (
    <div className="mb-8 flex items-center gap-4 p-6 rounded-xl border border-indigo-200 bg-indigo-50/60">
      <Loader2 size={24} className="animate-spin text-indigo-600 shrink-0" />
      <div>
        <p className="text-sm font-semibold text-indigo-900">
          {refreshing ? 'Refreshing your stack for your new role' : 'Designing your starter stack'}
        </p>
        <p className="text-sm text-indigo-700/80">
          We're building templates for your role and domain — usually under a minute.
          Browse the playbooks below in the meantime; your stack will appear here.
        </p>
      </div>
    </div>
  );
}

// ── Industry sidebar ──────────────────────────────────────────────────────────
function IndustryFilter({ industries, templates, selectedIndustry, onSelect }) {
  const counts = useMemo(() => {
    const c = {};
    templates.forEach((t) => {
      if (t.domain) c[t.domain] = (c[t.domain] || 0) + 1;
    });
    return c;
  }, [templates]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3 px-3">
        <h3 className="text-[11px] font-semibold tracking-wider uppercase text-gray-400">Industry</h3>
      </div>
      <div className="space-y-0.5">
        <button
          onClick={() => onSelect(null)}
          className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-sm ${
            selectedIndustry === null
              ? 'bg-indigo-50 text-indigo-700 font-medium'
              : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          <div className="flex items-center justify-between">
            <span>All</span>
            <span className="text-xs text-gray-400">{templates.length}</span>
          </div>
        </button>
        {industries
          .sort((a, b) => {
            // Featured industries first, then alphabetical
            const featured = ['Marketing'];
            const aFeat = featured.indexOf(a.label);
            const bFeat = featured.indexOf(b.label);
            if (aFeat !== -1 && bFeat === -1) return -1;
            if (aFeat === -1 && bFeat !== -1) return 1;
            if (aFeat !== -1 && bFeat !== -1) return aFeat - bFeat;
            return a.label.localeCompare(b.label);
          })
          .map((ind) => {
            const isFeatured = ind.label === 'Marketing';
            return (
              <button
                key={ind.key}
                onClick={() => onSelect(ind.label)}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-sm ${
                  selectedIndustry === ind.label
                    ? 'bg-indigo-50 text-indigo-700 font-medium'
                    : isFeatured
                    ? 'text-indigo-600 font-medium hover:bg-indigo-50'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    {ind.label}
                    {isFeatured && <span className="text-[10px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full font-semibold">NEW</span>}
                  </span>
                  <span className="text-xs text-gray-400">{counts[ind.key] || 0}</span>
                </div>
              </button>
            );
          })}
      </div>
    </div>
  );
}

// ── Detail modal ──────────────────────────────────────────────────────────────
function TemplateDetailModal({ templateId, onClose, onUse }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sampleOpen, setSampleOpen] = useState(false);

  useEffect(() => {
    if (!templateId) return;
    setLoading(true);
    fetchTemplateDetail(templateId)
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [templateId]);

  if (!templateId) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <div className="flex items-center justify-center p-16">
            <Loader2 size={28} className="animate-spin text-gray-400" />
          </div>
        ) : !detail ? (
          <div className="p-8 text-center text-gray-500">Failed to load template.</div>
        ) : (
          <div className="p-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">{detail.template_name}</h2>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm capitalize">
                    {detail.domain?.toLowerCase()}
                  </span>
                  {detail.role && (
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm">
                      {detail.role}
                    </span>
                  )}
                  {detail.has_graph && (
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-lg text-sm flex items-center gap-1">
                      <Zap size={12} /> Ready to run
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                <X size={20} />
              </button>
            </div>

            {/* Description */}
            <p className="text-gray-700 mb-6 text-sm leading-relaxed">{detail.description}</p>

            {/* Stats row */}
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
              {detail.estimated_duration && (
                <span className="flex items-center gap-1">
                  <Clock size={14} />
                  ~{Math.ceil(detail.estimated_duration / 60)} min
                </span>
              )}
              {detail.node_count > 0 && (
                <span className="flex items-center gap-1">
                  <Layers size={14} />
                  {detail.node_count} steps
                </span>
              )}
            </div>

            {/* Input fields */}
            {detail.input_schema && detail.input_schema.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <PenLine size={15} className="text-gray-500" />
                  <h3 className="font-semibold text-gray-900 text-sm">What you'll provide</h3>
                </div>
                <div className="space-y-2">
                  {detail.input_schema.map((field) => (
                    <div
                      key={field.name}
                      className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="text-sm font-medium text-gray-800">{field.label}</span>
                          {field.required && (
                            <span className="text-red-500 text-xs font-bold">required</span>
                          )}
                        </div>
                        {field.placeholder && (
                          <p className="text-xs text-gray-500 italic">{field.placeholder}</p>
                        )}
                        {field.options && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            Options: {field.options.join(', ')}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 bg-white border border-gray-200 px-2 py-0.5 rounded capitalize">
                        {FIELD_TYPE_LABELS[field.type] || field.type}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sample output (collapsible) */}
            {detail.sample_output && (
              <div className="mb-6">
                <button
                  onClick={() => setSampleOpen((o) => !o)}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-sm font-semibold text-gray-700"
                >
                  <span className="flex items-center gap-2">
                    <FileText size={15} className="text-gray-500" />
                    Example output
                  </span>
                  {sampleOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {sampleOpen && (
                  <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                      {detail.sample_output}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <Button onClick={() => onUse(detail)} size="lg" className="flex-1">
                Use template
                <ChevronRight size={18} />
              </Button>
              <Button onClick={onClose} variant="ghost" size="lg">
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Delete confirm modal ──────────────────────────────────────────────────────
function DeleteConfirmModal({ template, onConfirm, onCancel, loading }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <Trash2 size={20} className="text-red-600" />
          </div>
          <h3 className="font-semibold text-gray-900">Delete template?</h3>
        </div>
        <p className="text-sm text-gray-600 mb-5">
          "<span className="font-medium">{template.template_name}</span>" will be permanently deleted.
        </p>
        <div className="flex gap-3">
          <Button onClick={onCancel} disabled={loading} variant="secondary" className="flex-1">
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={loading} variant="danger" className="flex-1">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main browser ──────────────────────────────────────────────────────────────
export default function AIWorkflowTemplateBrowser({ onSelectTemplate, refreshSignal = 0, onStartFromScratch }) {
  const [activeTab, setActiveTab] = useState('playbooks'); // 'playbooks' | 'mine' | 'all'
  const [playbooksTemplates, setPlaybooksTemplates] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [myTemplates, setMyTemplates] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [detailTemplateId, setDetailTemplateId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ── "Your stack" starter cards (brainstorm-z8t2.8) ────────────────────────
  const [starterTemplates, setStarterTemplates] = useState([]);
  const [profile, setProfile] = useState(null); // { role, group_function } | null while loading
  const [showWizard, setShowWizard] = useState(false);
  const [polling, setPolling] = useState(false);
  const pollTimersRef = useRef({ interval: null, timeout: null });
  // Regen-aware polling (z8t2.19): ids visible when the poll started, and
  // the profile slice we're waiting on ({groupFunction, role} | null).
  const pollPrevIdsRef = useRef(new Set());
  const pollTargetRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollTimersRef.current.interval) clearInterval(pollTimersRef.current.interval);
    if (pollTimersRef.current.timeout) clearTimeout(pollTimersRef.current.timeout);
    pollTimersRef.current.interval = null;
    pollTimersRef.current.timeout = null;
    setPolling(false);
  }, []);

  const pollForStarterCards = useCallback(() => {
    stopPolling();
    setPolling(true);
    pollTimersRef.current.interval = setInterval(async () => {
      try {
        const cards = await fetchTemplates(null, null, 'starter');
        // With per-profile retention (z8t2.20) the previous profile's cards
        // keep coming back, so "any cards" would latch the stale set
        // (z8t2.19). Succeed only when the target profile's slice contains
        // something we hadn't seen at poll start; without a target, any
        // cards do (fresh-user path).
        const target = pollTargetRef.current;
        const slice = target
          ? cards.filter(
              (t) => t.domain === target.groupFunction && (!target.role || t.role === target.role)
            )
          : cards;
        const isNew = slice.some((t) => !pollPrevIdsRef.current.has(t.id));
        if (slice.length > 0 && isNew) {
          setStarterTemplates(cards);
          stopPolling();
        }
      } catch {
        // keep polling — a transient failure shouldn't give up early
      }
    }, STARTER_POLL_INTERVAL_MS);
    pollTimersRef.current.timeout = setTimeout(() => {
      // Give up silently after the timeout — generic list remains visible.
      stopPolling();
    }, STARTER_POLL_TIMEOUT_MS);
  }, [stopPolling]);

  useEffect(() => stopPolling, [stopPolling]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [systemTpls, userTpls, starterTpls, profileRes] = await Promise.all([
        fetchTemplates(null, true),
        fetchTemplates(null, false),
        fetchTemplates(null, null, 'starter'),
        axios.get('/user/profile').catch(() => null),
      ]);
      setPlaybooksTemplates(systemTpls);
      // "All" = everything visible to the caller: starters + own + system,
      // deduped by id (starter cards also come back from the unfiltered list).
      const seenIds = new Set();
      setAllTemplates(
        [...starterTpls, ...userTpls, ...systemTpls].filter(
          (t) => !seenIds.has(t.id) && seenIds.add(t.id)
        )
      );
      setMyTemplates(userTpls);
      setStarterTemplates(starterTpls);
      // Build sidebar domains from all templates
      const domainSet = new Set([...systemTpls, ...userTpls].map((t) => t.domain).filter(Boolean));
      const inds = Array.from(domainSet).sort().map((d) => ({ key: d, label: d }));
      setIndustries(inds);

      const profileData = profileRes?.data || null;
      setProfile(profileData);
      const profileComplete = !!(profileData?.role && profileData?.group_function);
      if (profileComplete && starterTpls.length === 0) {
        // Generation is async — poll until cards arrive or we give up.
        pollForStarterCards();
      }
    } catch {
      setError('Failed to load templates. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [pollForStarterCards]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // First-run wizard completes up in MainApp — refetch profile + templates
  // (loadData's own logic starts the starter-card poll once the profile is
  // complete with zero cards).
  useEffect(() => {
    if (refreshSignal > 0) loadData();
  }, [refreshSignal, loadData]);

  const handleWizardComplete = useCallback((saved) => {
    setShowWizard(false);
    // Wait specifically for the NEW profile's cards; the retained previous
    // set would otherwise satisfy a naive "any cards" poll (z8t2.19/.20).
    pollPrevIdsRef.current = new Set(starterTemplates.map((t) => t.id));
    pollTargetRef.current = saved?.groupFunction
      ? { groupFunction: saved.groupFunction, role: saved.role }
      : null;
    loadData();
    pollForStarterCards();
  }, [pollForStarterCards, loadData, starterTemplates]);

  const profileComplete = !!(profile?.role && profile?.group_function);
  // Display slice: only the current profile's cards (retained sets from
  // previous profiles stay hidden but cached server-side, z8t2.20).
  const visibleStack = useMemo(() => {
    if (!profile?.group_function) return starterTemplates;
    const slice = starterTemplates.filter(
      (t) => t.domain === profile.group_function && (!profile.role || t.role === profile.role)
    );
    // Legacy sets predate exact role labels — fall back to domain match.
    if (slice.length > 0) return slice;
    return starterTemplates.filter((t) => t.domain === profile.group_function);
  }, [starterTemplates, profile]);
  const showFramingCard = !profileComplete && starterTemplates.length === 0;
  const showDesigningPlaceholder = profileComplete && polling;

  const filteredTemplates = useMemo(() => {
    let templates =
      activeTab === 'playbooks' ? playbooksTemplates :
      activeTab === 'mine' ? myTemplates :
      allTemplates;
    if (selectedIndustry && activeTab !== 'mine') {
      templates = templates.filter((t) => t.domain === selectedIndustry);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      templates = templates.filter(
        (t) =>
          t.template_name?.toLowerCase().includes(q) ||
          t.description?.toLowerCase().includes(q) ||
          t.tags?.some((tag) => tag.toLowerCase().includes(q))
      );
    }
    return templates;
  }, [playbooksTemplates, allTemplates, myTemplates, activeTab, selectedIndustry, searchQuery]);

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setDetailTemplateId(template.id);
  };

  const handleDeleteTemplate = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await deleteTemplate(deleteTarget.id);
      setMyTemplates((prev) => prev.filter((t) => t.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch {
      // keep modal open — user can retry
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleUseTemplate = (template) => {
    onSelectTemplate?.(template);
    setDetailTemplateId(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 size={22} className="animate-spin" />
          <span>Loading templates...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow p-8 text-center max-w-md">
          <AlertCircle size={40} className="mx-auto text-red-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Couldn't load templates</h3>
          <p className="text-gray-500 mb-4 text-sm">{error}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Your stack — personalized starter cards, rendered above everything else. */}
        {visibleStack.length > 0 && (
          <div className="mb-8">
            <div className="mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Your stack</h2>
              <p className="text-sm text-gray-500">Starter templates designed for your role and domain.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {visibleStack.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onSelect={handleSelectTemplate}
                  isSelected={selectedTemplate?.id === template.id}
                />
              ))}
            </div>
          </div>
        )}

        {showFramingCard && (
          <FramingCard onSetupStack={() => setShowWizard(true)} />
        )}

        {showDesigningPlaceholder && (
          <DesigningStackPlaceholder refreshing={starterTemplates.length > 0} />
        )}

        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
          <h1 className="text-3xl font-semibold text-gray-900 mb-1.5">
            {activeTab === 'mine' ? 'My Templates' :
             activeTab === 'playbooks' ? 'Playbooks' :
             'All Templates'}
          </h1>
          <p className="text-gray-500 text-[15px] max-w-2xl">
            {activeTab === 'mine'
              ? 'Workflows you\'ve saved as reusable templates.'
              : activeTab === 'playbooks'
              ? 'Pre-validated, execution-ready workflows. Pick one, fill in your context, run.'
              : 'Community and org templates. Discover, remix, and save your own.'}
          </p>
          </div>
          <div className="shrink-0 mt-1 flex items-center gap-2">
            {onStartFromScratch && (
              <button
                onClick={onStartFromScratch}
                className="px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus-visible:ring-2 focus-visible:ring-indigo-500 transition-colors"
              >
                Start from scratch
              </button>
            )}
            <button
              onClick={() => setShowWizard(true)}
              className="px-3 py-1.5 text-sm text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-50 focus-visible:ring-2 focus-visible:ring-indigo-500 transition-colors"
            >
              Change role & domain
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-5 border-b border-gray-200">
          <button
            onClick={() => { setActiveTab('playbooks'); setSelectedIndustry(null); }}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'playbooks'
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            <ShieldCheck size={15} />
            Playbooks
            {playbooksTemplates.length > 0 && (
              <span className={`text-xs font-normal tabular-nums ${activeTab === 'playbooks' ? 'text-indigo-500' : 'text-gray-400'}`}>
                {playbooksTemplates.length}
              </span>
            )}
          </button>
          <button
            onClick={() => { setActiveTab('mine'); setSelectedIndustry(null); }}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'mine'
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            <Bookmark size={15} />
            My Templates
            {myTemplates.length > 0 && (
              <span className={`text-xs font-normal tabular-nums ${activeTab === 'mine' ? 'text-indigo-500' : 'text-gray-400'}`}>
                {myTemplates.length}
              </span>
            )}
          </button>
          <button
            onClick={() => { setActiveTab('all'); setSelectedIndustry(null); }}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'all'
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            <Globe size={15} />
            All
            {allTemplates.length > 0 && (
              <span className={`text-xs font-normal tabular-nums ${activeTab === 'all' ? 'text-indigo-500' : 'text-gray-400'}`}>
                {allTemplates.length}
              </span>
            )}
          </button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={activeTab === 'mine' ? 'Search your templates...' : activeTab === 'playbooks' ? 'Search playbooks...' : 'Search all templates...'}
              className="w-full pl-11 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-sm"
            />
          </div>
        </div>

        <div className={`grid grid-cols-1 gap-6 ${activeTab !== 'mine' ? 'lg:grid-cols-4' : ''}`}>
          {/* Sidebar — on Playbooks and All tabs */}
          {activeTab !== 'mine' && (
            <div className="lg:col-span-1">
              <IndustryFilter
                industries={industries}
                templates={activeTab === 'playbooks' ? playbooksTemplates : allTemplates}
                selectedIndustry={selectedIndustry}
                onSelect={setSelectedIndustry}
              />
            </div>
          )}

          {/* Grid */}
          <div className={activeTab !== 'mine' ? 'lg:col-span-3' : ''}>
            {activeTab === 'mine' && myTemplates.length === 0 && !searchQuery ? (
              <div className="py-16 text-center">
                <Bookmark size={32} className="mx-auto text-gray-300 mb-4" strokeWidth={1.5} />
                <h3 className="text-base font-medium text-gray-700 mb-1">No saved templates yet</h3>
                <p className="text-gray-500 text-sm max-w-sm mx-auto">
                  Complete a workflow and click "Save as Template" to reuse it here.
                </p>
              </div>
            ) : activeTab === 'playbooks' && playbooksTemplates.length === 0 && !searchQuery ? (
              <div className="py-16 text-center">
                <ShieldCheck size={32} className="mx-auto text-gray-300 mb-4" strokeWidth={1.5} />
                <h3 className="text-base font-medium text-gray-700 mb-1">No playbooks yet</h3>
                <p className="text-gray-500 text-sm max-w-sm mx-auto">
                  Playbooks are pre-decomposed, validated workflows. None have been seeded for your organisation yet.
                </p>
              </div>
            ) : activeTab === 'all' && allTemplates.length === 0 && !searchQuery ? (
              <div className="py-16 text-center">
                <Globe size={32} className="mx-auto text-gray-300 mb-4" strokeWidth={1.5} />
                <h3 className="text-base font-medium text-gray-700 mb-1">Nothing shared yet</h3>
                <p className="text-gray-500 text-sm max-w-sm mx-auto">
                  Community and org templates will appear here as they are published.
                </p>
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray-500">
                No matches. Try adjusting your search{activeTab !== 'mine' ? ' or industry filter' : ''}.
              </div>
            ) : (
              <>
                {/* Result count only shown when filtered (search or industry) so the user
                    knows their filter is narrowing — otherwise the tab badge already covers it. */}
                {(searchQuery || selectedIndustry) && (
                  <p className="mb-3 text-xs text-gray-400 tabular-nums">
                    {filteredTemplates.length} {activeTab === 'playbooks' ? `playbook${filteredTemplates.length !== 1 ? 's' : ''}` : `template${filteredTemplates.length !== 1 ? 's' : ''}`}
                  </p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredTemplates.map((template) => (
                    <TemplateCard
                      key={template.id}
                      template={template}
                      onSelect={handleSelectTemplate}
                      isSelected={selectedTemplate?.id === template.id}
                      onDelete={activeTab === 'mine' ? () => setDeleteTarget(template) : undefined}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {detailTemplateId && (
        <TemplateDetailModal
          templateId={detailTemplateId}
          onClose={() => setDetailTemplateId(null)}
          onUse={handleUseTemplate}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          template={deleteTarget}
          onConfirm={handleDeleteTemplate}
          onCancel={() => setDeleteTarget(null)}
          loading={deleteLoading}
        />
      )}

      {showWizard && (
        <OnboardingWizard onComplete={handleWizardComplete} />
      )}
    </div>
  );
}
